import React, { useState, useEffect, lazy, Suspense } from 'react';
import { AccessibilityProvider } from './components/AccessibilityContext';
import AccessibilityPanel from './components/AccessibilityPanel';
import Navigation from './components/Navigation';
import ErrorBoundary from './components/ErrorBoundary';
import { pageview } from './gtag'; // Import pageview function

// Lazy-loaded components
const HeroSection = lazy(() => import('./components/HeroSection'));
const ServicesSection = lazy(() => import('./components/ServicesSection'));
const FeaturesSection = lazy(() => import('./components/FeaturesSection'));
const ContactSection = lazy(() => import('./components/ContactSection'));
const Footer = lazy(() => import('./components/Footer'));

// Loading fallback component
const SectionLoader = () => (
  <div className="flex items-center justify-center min-h-[300px]">
    <div className="animate-pulse flex flex-col items-center">
      <div className="w-12 h-12 bg-gradient-to-br from-teal-200 to-teal-300 rounded-full mb-4"></div>
      <div className="h-4 w-24 bg-gray-200 rounded"></div>
    </div>
  </div>
);

function App() {
  const [activeSection, setActiveSection] = useState('hero');
  const [showAccessibilityPanel, setShowAccessibilityPanel] = useState(false);

  useEffect(() => {
    // Track initial page view
    pageview(window.location.pathname + window.location.search);

    const handleScroll = () => {
      const sections = ['hero', 'services', 'features', 'contact'];
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const offsetTop = element.offsetTop;
          const height = element.offsetHeight;
          
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + height) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Call once to set initial active section
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <ErrorBoundary>
      <AccessibilityProvider>
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-cyan-50 to-teal-50">
          {/* Skip to main content link for screen readers */}
          <a
            href="#main-content"
            className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-blue-600 text-white px-4 py-2 rounded-lg z-50"
          >
            דלג לתוכן הראשי
          </a>
          
          <Navigation 
            activeSection={activeSection} 
            setActiveSection={setActiveSection}
            showAccessibilityPanel={showAccessibilityPanel}
            setShowAccessibilityPanel={setShowAccessibilityPanel}
          />
          
          <main id="main-content" role="main">
            <ErrorBoundary>
              <Suspense fallback={<SectionLoader />}>
                <HeroSection />
              </Suspense>
            </ErrorBoundary>
            
            <ErrorBoundary>
              <Suspense fallback={<SectionLoader />}>
                <ServicesSection />
              </Suspense>
            </ErrorBoundary>
            
            <ErrorBoundary>
              <Suspense fallback={<SectionLoader />}>
                <FeaturesSection />
              </Suspense>
            </ErrorBoundary>
            
            <ErrorBoundary>
              <Suspense fallback={<SectionLoader />}>
                <ContactSection />
              </Suspense>
            </ErrorBoundary>
          </main>
          
          <ErrorBoundary>
            <Suspense fallback={<SectionLoader />}>
              <Footer />
            </Suspense>
          </ErrorBoundary>

          {/* Accessibility Panel - Moved outside navigation */}
          <AccessibilityPanel 
            isOpen={showAccessibilityPanel} 
            onClose={() => setShowAccessibilityPanel(false)} 
          />
        </div>
      </AccessibilityProvider>
    </ErrorBoundary>
  );
}

export default App;